import { Node, mergeAttributes } from '@tiptap/core';
import { VueNodeViewRenderer } from '@tiptap/vue-3';
import ResizableMediaView from './ResizableMediaView.vue';

export interface IframeOptions {
  allowFullscreen: boolean;
  HTMLAttributes: Record<string, string>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    iframe: {
      setIframe: (options: {
        src: string;
        width?: string | null;
      }) => ReturnType;
    };
  }
}

export const Iframe = Node.create<IframeOptions>({
  name: 'iframe',

  group: 'block',

  atom: true,

  addOptions() {
    return {
      allowFullscreen: true,
      HTMLAttributes: {
        class: 'iframe-wrapper',
      },
    };
  },

  addAttributes() {
    return {
      src: {
        default: null,
      },
      width: {
        default: null,
        parseHTML: (element) => {
          // When parsing, the matched element is the <iframe> itself.
          // Width is stored on the parent wrapper div as an inline style.
          const parent = element.parentElement;
          if (parent?.style.width) {
            const w = parent.style.width;
            if (w.endsWith('%')) {
              return w;
            }

            return w;
          }
          const w = element.getAttribute('width');

          return w || null;
        },
        // Don't render on the iframe element — handled in node renderHTML
        renderHTML: () => ({}),
      },
      frameborder: {
        default: '0',
      },
      allowfullscreen: {
        default: this.options.allowFullscreen,
        parseHTML: () => this.options.allowFullscreen,
      },
    };
  },

  parseHTML() {
    return [{ tag: 'iframe' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const width = node.attrs.width;
    const wrapperStyle = width
      ? `width: ${width}; max-width: 100%;`
      : 'max-width: 100%;';

    return [
      'div',
      mergeAttributes(this.options.HTMLAttributes, { style: wrapperStyle }),
      [
        'iframe',
        mergeAttributes(HTMLAttributes, {
          style:
            'width: 100%; aspect-ratio: 16/9; border: none; border-radius: 4px;',
        }),
      ],
    ];
  },

  addNodeView() {
    return VueNodeViewRenderer(ResizableMediaView);
  },

  addCommands() {
    return {
      setIframe:
        (options) =>
        ({ tr, dispatch }) => {
          const { selection } = tr;
          const node = this.type.create(options);

          if (dispatch) {
            tr.replaceRangeWith(selection.from, selection.to, node);
          }

          return true;
        },
    };
  },
});
